import React, { useState, useContext, useEffect, useRef } from "react";
import Select from "react-select";
import { FilterContext } from "../FilterContextProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Button from "../Components/Button";
import Input from "../Components/Input";
import ReportsLayout from "./ReportsLayout";
import useEvents from "../hooks/useEvents";
import useAllEvents from "../hooks/useAllEvents";
import { exportToCSV } from "../Utility/Csv";
import { Link } from "react-router-dom";
import ReactToPrint from "react-to-print";

const Cash = () => {
  const { filterData } = useContext(FilterContext);
  const printRef = useRef();
  const [formatPrint, setFormatPrint] = useState(false);

  const {
    events,
    pagy,
    setCurrentPage,
    eventDates,
    eventDateOptions,
    handleEventDateSelect,
    renderCustomEventDateSelector,
    sortFilter,
    setSortFilter,
    resetPagination,
    setSearchFilter,
  } = useEvents("show", "Artist");

  const { allEvents, setAllEventsSearch, setAllEventsSort } = useAllEvents(
    "show",
    "Artist"
  );

  const sortOptions = [
    {
      label: "Date",
      value: "date",
    },
    {
      label: "City",
      value: "city",
    },
    {
      label: "State",
      value: "state",
    },
    {
      label: "Venue",
      value: "name",
    },
    {
      label: "Recently added",
      value: "created_at",
    },
  ];

  const handleSortSelect = (option) => {
    if (option.value !== sortFilter) {
      setSortFilter(option.value);
      setAllEventsSort(option.value);
      resetPagination();
      setCurrentPage(1);
    }
  };

  const [searchTimeout, setSearchTimeout] = useState();

  const handleSearchChange = (e) => {
    setSearchTimeout(e.target.value);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setSearchFilter(searchTimeout);
      setAllEventsSearch(searchTimeout);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTimeout, setSearchFilter, setAllEventsSearch]);

  const handlePageChange = (e) => {
    setCurrentPage(e.target.value);
  };

  const cashExport = allEvents.map((event) => ({
    Event_Date: event.formattedDate,
    City: event.city,
    State: event.state,
    Gross_Sales: event.gross_sales,
    Paid_to_Venue: event.cash_paid_to_venue,
    CC_Sales: event.cc_sales,
    Cash_Due: event.gross_sales - event.cash_paid_to_venue - event.cc_sales,
  }));

  const totals = {
    Event_Date: "",
    City: "",
    State: "",
    Gross_Sales: events.reduce((a, b) => {
      return a + b.gross_sales;
    }, 0),
    Paid_to_Venue: events.reduce((a, b) => {
      return a + b.cash_paid_to_venue;
    }, 0),
    CC_Sales: events.reduce((a, b) => {
      return a + b.cc_sales;
    }, 0),
    Cash_Due: events.reduce((a, b) => {
      return a + b.gross_sales - b.cash_paid_to_venue - b.cc_sales;
    }, 0),
  };

  const combined = cashExport.concat(totals);

  const handleFormatPrint = () => {
    setFormatPrint(true);
  };

  const renderSelectArtistMessage = () => {
    return (
      <div className="m-8 py-12 pl-4">
        Please select an artist from the filter in order to see events
      </div>
    );
  };

  const renderEventsTable = () => {
    return (
      <div>
        <div>
          <div className="max-w-max">
            <Link to="/reports">
              <div className="-mt-6 mb-6 font-medium text-black text-sm">
                <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to
                reports
              </div>
            </Link>
          </div>
          {!formatPrint && (
            <div className="text-xl my-6 font-medium">Cash on Hand</div>
          )}
          <div className="mb-4 text-sm">
            <div className="flex">
              <div className="absolute mt-6 ml-2">
                <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
              </div>
              <div className="w-1/4 text-xs">
                <Input
                  onChange={handleSearchChange}
                  value={searchTimeout}
                  placeholder="Search for events"
                  label="SEARCH"
                  bigPaddingLeft
                />
              </div>
              <div className="flex flex-col w-1/4 ml-2">
                <span className="text-xs">SORT BY</span>
                <Select
                  id="sort_filter"
                  className="text-xs font-light"
                  placeholder="Select sort by..."
                  options={sortOptions}
                  onChange={handleSortSelect}
                  value={sortOptions?.filter(
                    (each) => each.value === sortFilter
                  )}
                />
              </div>
              <div className="flex flex-col text-sm w-1/4 ml-2">
                <span className="text-xs">SELECT DATE RANGE</span>
                <Select
                  id="date"
                  className="text-xs font-light w-full"
                  placeholder="Select date range"
                  options={eventDateOptions}
                  onChange={handleEventDateSelect}
                  value={eventDateOptions?.filter(
                    (each) => each.value === eventDates.dateButton
                  )}
                />
              </div>
            </div>
            <div className="my-4 w-1/2">
              {eventDates.dateButton === "custom" &&
                renderCustomEventDateSelector()}
            </div>
          </div>
        </div>
        {events.length > 0 ? (
          <div ref={printRef} className={formatPrint ? "mx-8" : ""}>
            {formatPrint && (
              <div className="text-xl my-6 font-medium">Cash on Hand</div>
            )}
            <form>
              <table className="w-full">
                <thead className="text-xs font-light">
                  <tr className="text-left border-b-2 h-11">
                    <td className="w-1/8">DATE</td>
                    <td className="w-1/8">CITY</td>
                    <td className="w-thin">STATE</td>
                    <td className="w-1/8 text-right">GROSS SALES</td>
                    <td className="w-1/8 text-right">PAID TO VENUE</td>
                    <td className="w-1/8 text-right">CC SALES</td>
                    <td className="w-1/8 text-right">CASH DUE</td>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, i) => {
                    const eventToRender = event;
                    return (
                      <tr
                        key={eventToRender.id}
                        className="border-b text-sm h-11"
                      >
                        <td id={`date_${i}`}>{eventToRender.formattedDate}</td>
                        <td id={`city_${i}`}>{eventToRender?.city}</td>
                        <td id={`state_${i}`}>{eventToRender?.state}</td>
                        <td id={`gross_sales_${i}`} className="text-right">
                          {eventToRender.gross_sales
                            ? eventToRender.gross_sales?.toLocaleString(
                                "en-US",
                                {
                                  style: "currency",
                                  currency: "USD",
                                }
                              )
                            : 0?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                        </td>
                        <td
                          id={`cash_paid_to_venue_${i}`}
                          className="text-right"
                        >
                          {eventToRender.cash_paid_to_venue
                            ? eventToRender.cash_paid_to_venue?.toLocaleString(
                                "en-US",
                                {
                                  style: "currency",
                                  currency: "USD",
                                }
                              )
                            : 0?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                        </td>
                        <td id={`cc_sales_${i}`} className="text-right">
                          {eventToRender.cc_sales
                            ? eventToRender.cc_sales?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })
                            : 0?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                        </td>
                        <td id={`difference_${i}`} className="text-right">
                          {(
                            eventToRender.gross_sales -
                            eventToRender.cash_paid_to_venue -
                            eventToRender.cc_sales
                          )?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="font-semibold text-sm h-11">
                    <td></td>
                    <td></td>
                    <td></td>
                    <td id="gross_sales_totals" className="text-right">
                      {totals.Gross_Sales?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td id="cash_totals" className="text-right">
                      {totals.Paid_to_Venue?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td id="cash_totals" className="text-right">
                      {totals.CC_Sales?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td id="cash_totals" className="text-right">
                      {totals.Cash_Due?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>
          </div>
        ) : sortFilter ? (
          <div className="mx-12 py-16">
            No events match the provided filters
          </div>
        ) : (
          <div className="mx-12 py-16">
            No events have been added for this artist yet
          </div>
        )}
      </div>
    );
  };

  return (
    <ReportsLayout>
      <div>
        <div className="mt-8 mx-8 py-10 px-12 bg-white text-black rounded-lg border">
          {events.length > 0 && (
            <div className="flex justify-end text-sm">
              {formatPrint ? (
                <ReactToPrint
                  trigger={() => (
                    <Button className="button" text="Print"></Button>
                  )}
                  content={() => printRef.current}
                />
              ) : (
                <Button
                  onClick={handleFormatPrint}
                  text="Format Print"
                  className="button"
                ></Button>
              )}
              <div className="ml-2">
                <Button
                  className="button button-white"
                  text="Export"
                  onClick={() =>
                    exportToCSV(
                      combined,
                      `${filterData.artist_name}_cash_report`
                    )
                  }
                ></Button>
              </div>
            </div>
          )}
          {filterData.artist_id
            ? renderEventsTable()
            : renderSelectArtistMessage()}
        </div>
        {events.length > 0 && (
          <footer className="text-sm mt-4 mx-8 pb-4 flex justify-between">
            <div className="ml-1 mr-8">
              Showing <span className="font-bold">{pagy.from} </span>to
              <span className="font-bold"> {pagy.to} </span>of
              <span className="font-bold"> {pagy.count} </span>results
            </div>
            <div className="mr-8">
              Page {pagy.page} of {pagy.last}
            </div>
            <div className="flex -mt-2">
              <span>
                <Button
                  className={
                    pagy.page === 1
                      ? "button-white-disabled button-sm"
                      : "button button-white button-sm"
                  }
                  onClick={resetPagination}
                  text="First"
                  disabled={pagy.page === 1}
                ></Button>
              </span>
              <span className="mx-1">
                <Button
                  value={pagy.prev}
                  className={
                    !pagy.prev
                      ? "button-white-disabled button-sm"
                      : "button button-white button-sm"
                  }
                  onClick={handlePageChange}
                  text="Prev"
                  disabled={!pagy.prev}
                ></Button>
              </span>
              <span>
                <Button
                  value={pagy.next}
                  className={
                    !pagy.next
                      ? "button-white-disabled button-sm"
                      : "button button-white button-sm"
                  }
                  onClick={handlePageChange}
                  text="Next"
                  disabled={!pagy.next}
                ></Button>
              </span>
              <span className="ml-1">
                <Button
                  value={pagy.last}
                  className={
                    pagy.page === pagy.last
                      ? "button-white-disabled button-sm"
                      : "button button-white button-sm"
                  }
                  onClick={handlePageChange}
                  text="Last"
                  disabled={pagy.page === pagy.last}
                ></Button>
              </span>
            </div>
          </footer>
        )}
      </div>
    </ReportsLayout>
  );
};

export default Cash;
